import React, { useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { BorderRadius } from '../../styles/constants';
import { ProjectForm } from './projectForm';
import { Project } from '../../models/project';
import { editProject } from '../../services/api/projectService';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import useAppSnackbar from '../../hooks/useAppSnackbar';

interface Props {
  isOpen: boolean;
  onCloseEditDialog(): void;
  project: Project | null;
  onRequestSuccessful(): void;
}

export function ProjectDialogEdit({
  isOpen,
  onCloseEditDialog,
  project,
  onRequestSuccessful,
}: Props): JSX.Element {
  const { onErrorSnackbar } = useAppSnackbar();

  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    onCloseEditDialog();
    setIsOpenConfirmation(false);
  }, [onCloseEditDialog]);

  const handleEditProject = useCallback(
    (editProjectInfo) => {
      setIsLoadingRequest(true);

      if (project) {
        const oldProjectInfo = {
          code: project.code,
          name: project.name,
          transferIndex: project.transferIndex,
          ongoing: project.ongoing,
          projectOwnerId: project.projectOwner.id,
          salesManagerId: project.salesManager.id,
          clientId: project.client.id,
          responsibleClientName: project.responsibleClientName,
          responsibleClientEmail: project.responsibleClientEmail,
          isAMS: project.isAMS,
          towerId: project.tower.id,
        };

        const editedProjectInfo = {
          code: editProjectInfo.code,
          transferIndex: editProjectInfo.transferIndex,
          name:
            typeof editProjectInfo.name === 'string'
              ? editProjectInfo.name
              : editProjectInfo.name.label,
          ongoing: editProjectInfo.ongoing,
          projectOwnerId: editProjectInfo.projectOwner.id,
          salesManagerId: editProjectInfo.budgetOwner.id,
          clientId: editProjectInfo.client.id,
          responsibleClientName: editProjectInfo.responsibleClientName,
          responsibleClientEmail: editProjectInfo.responsibleClientEmail,
          isAMS: editProjectInfo.isAMS,
          towerId: editProjectInfo.tower.id,
        };

        const newProject = {
          ...oldProjectInfo,
          ...editedProjectInfo,
        };

        editProject(project.id, newProject)
          .then(() => {
            handleClose();
            onRequestSuccessful();
            setIsOpenConfirmation(true);
          })
          .then(() => {
            setTimeout(() => {
              setIsOpenConfirmation(false);
            }, 1000);
          })
          .catch((error) => {
            return onErrorSnackbar(error as Error);
          })
          .finally(() => setIsLoadingRequest(false));
      }
    },
    [handleClose, project, onErrorSnackbar, onRequestSuccessful]
  );

  return (
    <>
      <Dialog open={isOpen} sx={{ borderRadius: BorderRadius.SMALL }}>
        <AppDialogTitle title="Edit Project" handleClose={handleClose} />
        <ProjectForm
          onCancel={handleClose}
          onSubmit={handleEditProject}
          project={project}
          isLoading={isLoadingRequest}
        />
      </Dialog>
      <AppConfirmationSuccessful
        isOpen={isOpenConfirmation}
        message="Record successfully updated"
        onClose={handleClose}
      />
    </>
  );
}
